// Seed: the prewritten workflow definitions of docs/engines/workflows.md §
// Catalogue, in the order a pull travels through the product (data in → flag
// → investigate → escalate → rank → report → knowledge). Definitions are code,
// registered at bootstrap; this list is that registry, not run data. Every run
// fact on the Workflows page is derived at the cursor from the seed each
// definition's `runs` source names (`modules/projects/WorkflowCatalogue.transform.ts`).
//
// The investigation's name and trigger, and each report's name and cadence,
// are read from their owners (`InvestigationRun.transform.ts`,
// `REPORT_TEMPLATES`) by the transform; the placeholders here are never shown.

import type { WorkflowDefinition } from '@/versions/v1/schema/workflow';

export const WORKFLOW_DEFINITIONS: readonly WorkflowDefinition[] = [
  {
    id: 'analytics.scheduled-pull',
    name: 'Analytics pull',
    verb: 'evaluate',
    actor: 'Engine',
    triggers: [{ kind: 'schedule', label: 'tag-map cadence' }],
    writes: 'Analysis results · excursions',
    runs: 'analysis-frames',
    runSegment: 'advisory',
  },
  {
    id: 'advisory.alarm-flag',
    name: 'Alarm flagging',
    verb: 'flag',
    actor: 'Decision model',
    triggers: [
      { kind: 'event', label: 'a pull lands an excursion' },
      { kind: 'schedule', label: 'sweep' },
    ],
    writes: 'Cases · variation events',
    runs: 'cases',
    runSegment: 'advisory',
  },
  {
    id: 'advisory.excursion-investigation',
    name: 'Excursion investigation',
    verb: 'investigate',
    actor: 'Agent',
    triggers: [
      { kind: 'event', label: 'a case reaches Diagnosis' },
      { kind: 'manual', label: 'from the case' },
    ],
    writes: 'Agent trace · recommendation',
    runs: 'investigation',
    runSegment: 'advisory',
  },
  {
    id: 'advisory.ladder-clock',
    name: 'Escalation clock',
    verb: 'escalate',
    actor: 'System',
    triggers: [{ kind: 'event', label: 'a case enters a TARP ladder' }],
    writes: 'Ladder tier · escalation record',
    runs: 'ladder',
    runSegment: 'advisory',
  },
  {
    id: 'advisory.ladder-sweep',
    name: 'Escalation sweep',
    verb: 'sweep',
    actor: 'System',
    // ADR-0008 catch-all: 15 min bounds how late a missed escalation lands (docs/engines/workflows.md).
    triggers: [{ kind: 'schedule', label: 'every 15 min' }],
    everyMinutes: 15,
    writes: 'Missed escalations',
    runs: 'ladder-sweep',
    runSegment: 'advisory',
  },
  {
    id: 'overview.priority-actions',
    name: 'Priority actions',
    verb: 'rank',
    actor: 'Decision model',
    triggers: [
      { kind: 'schedule', label: 'every 30 min' },
      { kind: 'event', label: 'after alarm flagging' },
    ],
    // Auto-refresh cadence: generated 11:30 → next 12:00 (AI_PANEL_META).
    everyMinutes: 30,
    writes: 'Recommended action order',
    runs: 'priority-refresh',
    runSegment: 'advisory',
  },
  {
    id: 'reports.daily-management',
    name: 'Daily management report',
    verb: 'narrate',
    actor: 'Agent',
    triggers: [{ kind: 'schedule', label: '' }],
    writes: 'Report instance · email',
    runs: 'report',
    runSegment: 'reports',
    reportTemplateId: 'tpl-daily-management',
  },
  {
    id: 'reports.daily-accounting',
    name: 'Daily accounting report',
    verb: 'narrate',
    actor: 'Agent',
    triggers: [{ kind: 'schedule', label: '' }],
    writes: 'Report instance · email',
    runs: 'report',
    runSegment: 'reports',
    reportTemplateId: 'tpl-daily-accounting',
  },
  {
    id: 'reports.weekly-summary',
    name: 'Weekly summary',
    verb: 'narrate',
    actor: 'Agent',
    triggers: [{ kind: 'schedule', label: '' }],
    writes: 'Report instance',
    runs: 'report',
    runSegment: 'reports',
    reportTemplateId: 'tpl-weekly-summary',
  },
  {
    id: 'reports.met-shift',
    name: 'Met shift report',
    verb: 'narrate',
    actor: 'Agent',
    triggers: [{ kind: 'schedule', label: '' }],
    writes: 'Report instance',
    runs: 'report',
    runSegment: 'reports',
    reportTemplateId: 'tpl-met-shift',
  },
  {
    id: 'ingest.fmea-workbook',
    name: 'FMEA workbook ingest',
    verb: 'transform',
    actor: 'System',
    triggers: [{ kind: 'event', label: 'a new workbook revision' }],
    writes: 'Failure modes · proposed rules',
    runs: 'rule-set',
    runSegment: 'advisory',
  },
];
